import { requestMutation } from '../lib/fasterdom/fasterdom';
import { hex2rgb } from './colors';

// Recolors the whole-app accent at runtime by overriding the `$color-primary` family from
// `src/styles/_variables.scss` (buttons, links, active tab, checkmarks, selected chat). `switchTheme`
// rewrites these same variables inline on the root on every theme change, so the override ships as an
// injected `:root { … !important }` stylesheet — that beats inline styles and survives theme switches.
// SCSS `color.mix` can't run in the browser, so the darker shades are computed here in JS.

const STYLE_ELEMENT_ID = 'accent-color-override';

type Rgb = [number, number, number];

// Mirrors `color.mix($color, $color-black, <ratio>%)`: `ratio`% of the color plus the rest black.
function mixWithBlack([r, g, b]: Rgb, ratio: number): Rgb {
  return [Math.round(r * ratio), Math.round(g * ratio), Math.round(b * ratio)];
}

export default function switchAccentColor(accentColor: string | undefined) {
  requestMutation(() => {
    const existing = document.getElementById(STYLE_ELEMENT_ID);

    if (!accentColor) {
      existing?.remove();
      return;
    }

    const rgb = hex2rgb(accentColor);
    const [r, g, b] = rgb;
    const shade = mixWithBlack(rgb, 0.92).join(',');
    const shadeDarker = mixWithBlack(rgb, 0.84).join(',');

    const css = `:root {
  --color-primary: ${accentColor} !important;
  --color-primary-shade: rgb(${shade}) !important;
  --color-primary-shade-darker: rgb(${shadeDarker}) !important;
  --color-primary-shade-rgb: ${shade} !important;
  --color-primary-opacity: rgba(${r}, ${g}, ${b}, 0.2) !important;
  --color-primary-opacity-hover: rgba(${r}, ${g}, ${b}, 0.25) !important;
  --color-primary-tint: rgba(${r}, ${g}, ${b}, 0.1) !important;
  --color-chat-active: ${accentColor} !important;
}`;

    const style = existing instanceof HTMLStyleElement ? existing : document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = css;
    if (!style.isConnected) document.head.appendChild(style);
  });
}
